import React, { useEffect, useState } from 'react';
import MyAppText from '../../../components/MyAppText';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import CustomButton from '../../../components/CustomButton';
import { useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { DeacParamList } from '../../../typings/Params';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { Pagination } from '../../../components/Pagination';
import MyDataTable from '../../../components/MyDataTable';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { deacService } from '../../../services/central-api/deac';
import Badge from '../../../components/Badge';
import { DEFAULT_PASTEL_COLORS, DEFAULT_PASTEL_TEXT_COLORS } from '../../../styles/colorPalettes';
import Filter from './Filters';
import Tooltip from '../../../components/Tooltip';
import { GenerateVacanciesModal } from './GenerateVacanciesModal';
import DeleteVacancyTemplateModal from './DeleteVacancyTemplateModal';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import Toast from 'react-native-toast-message';

export default function VacanciesTemplates({ navigation }: { navigation: Navigation; }) {
    const { theme } = useStyles();
    const route = useRoute<RouteProp<DeacParamList, 'VacanciesTemplates'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DeacParamList['VacanciesTemplates']>({
        ...route.params,
        page: Math.max(+route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [selectedTemplate, setSelectedTemplate] = useState<VacancyTemplate | undefined>(undefined);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [displayGenerateVacanciesModal, setDisplayGenerateVacanciesModal] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['deacVacanciesTemplates', filters],
        queryFn: () => deacService.getVacanciesTemplates(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch, isError, error } = queryResult;

    useEffect(() => {
        if (isError) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }, [error, isError]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    function getTagTextColor(color: string) {
        const index = DEFAULT_PASTEL_COLORS.indexOf(color);
        return DEFAULT_PASTEL_TEXT_COLORS[index];
    }

    return (
        <Container>
            <Header
                title={translate('vacanciesTemplatesManagement')}
                actions={<CustomButton
                    text={translate('registerTemplate')}
                    onPress={() => {
                        setSelectedTemplate(undefined);
                        navigation.navigate('VacancyTemplateForm');
                    }}
                />}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredVacanciesTemplates')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                    filter={<Filter
                        filters={filters}
                        setFilters={setFilters}
                        textFilter={textFilter}
                        setTextFilter={setTextFilter}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('id'),
                                cell: row => row.id,
                                minWidth: '75px',
                                grow: 0,
                            },
                            {
                                name: translate('name'),
                                cell: row => row.templateName,
                                grow: 1,
                            },
                            {
                                name: translate('activity'),
                                cell: row => row.title,
                                grow: 1,
                            },
                            {
                                name: translate('workplace'),
                                cell: row => row.workplace.name,
                                grow: 1,
                            },
                            {
                                name: translate('actingBody'),
                                cell: row => row.workplace.actingBodyUnit.actingBody.name,
                                grow: 1,
                            },
                            {
                                name: translate('command'),
                                cell: row => row.workplace.actingBodyCommand.name,
                                grow: 1,
                            },
                            {
                                name: translate('unit'),
                                cell: row => row.workplace.actingBodyUnit.name,
                                grow: 1,
                            },
                            {
                                name: translate('level'),
                                selector: row => row.levels?.map(l => l.name).join(', ') ?? '',
                                wrap: true,
                                grow: 1,
                            },
                            {
                                name: translate('tags'),
                                cell: row =>
                                    row.tags && row.tags.length > 0 ? <Tooltip
                                        useSimpleBox
                                        style={{
                                            color: theme.colors.white['500'],
                                            padding: 16,
                                            borderRadius: 12,
                                            justifyContent: 'center',
                                            zIndex: 9999,
                                            width: row.tags.length >= 6 ? 450 : 150,
                                            background: theme.colors.neutral['500'],
                                        }}
                                        tooltipContent={<View style={{ flexDirection: 'row', flexWrap: 'wrap', width: '100%', gap: 5 }}>
                                            {row.tags?.map(tag => (
                                                <Badge mode='ROUNDED' key={tag.id} backgroundColor={tag.color} size='small' textColor={getTagTextColor(tag.color)}>{tag.name}</Badge>
                                            ))}
                                        </View>}
                                        tooltipDirection='left'
                                    >
                                        <MyAppText
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore
                                            style={{ cursor: 'pointer', fontWeight: '600', fontSize: 14 }}
                                        >
                                            {translate('viewTags').toLowerCase()}
                                        </MyAppText>
                                    </Tooltip> : null,
                                wrap: true,
                                grow: 0,
                            },
                            {
                                name: '',
                                cell: row => {

                                    const actions: Action[] = [];

                                    actions.push({
                                        content: translate('generateVacancies'),
                                        onPress: () => {
                                            setSelectedTemplate(row);
                                            setDisplayGenerateVacanciesModal(true);
                                        }
                                    }, {
                                        content: translate('editInformation'),
                                        onPress: () => {
                                            setSelectedTemplate(row);
                                            navigation.navigate('VacancyTemplateForm', { id: row.id });
                                        }
                                    }, {
                                        content: translate('delete'),
                                        variant: 'danger',
                                        onPress: () => {
                                            setSelectedTemplate(row);
                                            setIsDeleteModalVisible(true);
                                        },
                                    });
                                    return (
                                        <DropdownButton actions={actions} />
                                    );
                                },
                                wrap: true,
                                button: true
                            }
                        ]}
                        data={data?.rows || []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={data?.count || 0}
                        setPage={page => {
                            setFilters(old => ({ ...old, page }));
                            navigation.setParams({ ...route.params, page: page + 1 });
                        }}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                            navigation.setParams({ ...route.params, limit: pageSize, page: 1 });
                        }}
                    />}
                />
            </Card>
            {selectedTemplate && <DeleteVacancyTemplateModal
                onConfirm={refetch}
                selectedTemplate={selectedTemplate}
                isModalVisible={isDeleteModalVisible}
                setModalVisible={setIsDeleteModalVisible}
            />}

            {selectedTemplate && <GenerateVacanciesModal
                onConfirm={() => { }}
                template={selectedTemplate}
                isModalVisible={displayGenerateVacanciesModal}
                setModalVisible={setDisplayGenerateVacanciesModal}
            />}
        </Container>
    );
}
